import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { ConfirmDialog } from './ConfirmDialog';
import { useT, useFormatDate } from '../i18n';

export interface Communication {
  id: number;
  participant_id: number;
  measure_id: number | null;
  kind: string;
  related_adjustment_id: number | null;
  to_name: string | null;
  to_email: string | null;
  subject: string;
  body: string;
  generated_at: string;
  generated_by: string | null;
  sent_at: string | null;
  sent_marked_by: string | null;
}

interface Props {
  participantId: number;
  /** Bumped by the parent when a new comm is created elsewhere, to trigger re-fetch. */
  refreshSignal?: number;
  /** Called with the unsent count after each load so the tab badge can update. */
  onUnsentCount?: (n: number) => void;
}

export function CommunicationsPanel({ participantId, refreshSignal, onUnsentCount }: Props) {
  const { t } = useT();
  const fd = useFormatDate();
  const [comms, setComms] = useState<Communication[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actingId, setActingId] = useState<number | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<{ subject: string; body: string }>({ subject: '', body: '' });
  const [confirming, setConfirming] = useState<
    { kind: 'mark-sent' | 'delete'; comm: Communication } | null
  >(null);

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getParticipantCommunications(participantId) as any;
      const list: Communication[] = data?.communications ?? [];
      setComms(list);
      onUnsentCount?.(list.filter(c => c.sent_at == null).length);
    } catch (e: any) {
      setError(e?.message || t('comms.errLoad'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participantId, refreshSignal]);

  async function copyBody(c: Communication) {
    const text = `${t('comms.copySubjectPrefix')}: ${c.subject}\n\n${c.body}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(c.id);
      setTimeout(() => setCopiedId(prev => (prev === c.id ? null : prev)), 1500);
    } catch {
      // Fallback: select + execCommand
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* swallow */ }
      document.body.removeChild(ta);
      setCopiedId(c.id);
      setTimeout(() => setCopiedId(prev => (prev === c.id ? null : prev)), 1500);
    }
  }

  function startEdit(c: Communication) {
    setEditingId(c.id);
    setEditDraft({ subject: c.subject, body: c.body });
  }

  function cancelEdit() {
    setEditingId(null);
    setEditDraft({ subject: '', body: '' });
  }

  async function saveEdit(c: Communication) {
    setActingId(c.id);
    try {
      await api.updateCommunication(c.id, { subject: editDraft.subject, body: editDraft.body });
      setEditingId(null);
      await reload();
    } catch (e: any) {
      setError(e?.message || t('comms.errSave'));
    } finally {
      setActingId(null);
    }
  }

  async function performAction() {
    if (!confirming) return;
    const c = confirming.comm;
    const kind = confirming.kind;
    setActingId(c.id);
    try {
      if (kind === 'mark-sent') await api.markCommunicationSent(c.id);
      else await api.deleteCommunication(c.id);
      setConfirming(null);
      await reload();
    } catch (e: any) {
      setError(e?.message || (kind === 'mark-sent' ? t('comms.errMarkSent') : t('comms.errDelete')));
    } finally {
      setActingId(null);
    }
  }

  if (loading && comms == null) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--s32)' }}><div className="spinner" /></div>;
  }
  if (error) {
    return <div style={{ color: 'var(--err)', padding: 'var(--s12)' }}>{error}</div>;
  }
  if (!comms || comms.length === 0) {
    return (
      <div style={{
        padding: 'var(--s24)', textAlign: 'center',
        color: 'var(--ink-3)', fontSize: 'var(--t-13)',
      }}>
        {t('comms.emptyParticipant')}
        <div style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)', marginTop: 'var(--s8)' }}>
          {t('comms.emptyParticipantHint')}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s12)' }}>
      {comms.map(c => {
        const isSent = c.sent_at != null;
        const isEditing = editingId === c.id;
        return (
          <div
            key={c.id}
            style={{
              border: '1px solid var(--line)',
              borderRadius: 'var(--r1)',
              background: 'var(--surface)',
              overflow: 'hidden',
            }}
          >
            {/* Header strip */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: 'var(--s8) var(--s12)',
              background: isSent ? 'color-mix(in srgb, var(--ok) 8%, var(--surface))' : 'var(--bg)',
              borderBottom: '1px solid var(--line)',
              gap: 'var(--s8)', flexWrap: 'wrap',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s8)', flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
                {isEditing ? (
                  <input
                    type="text"
                    value={editDraft.subject}
                    onChange={(e) => setEditDraft(d => ({ ...d, subject: e.target.value }))}
                    style={{
                      flex: 1, minWidth: 200,
                      padding: 'var(--s4) var(--s8)',
                      borderRadius: 'var(--r2)',
                      border: '1px solid var(--accent)',
                      background: 'var(--surface)',
                      fontSize: 'var(--t-13)',
                      fontWeight: 600,
                      color: 'var(--ink-1)',
                    }}
                    placeholder={t('comms.copySubjectPrefix')}
                  />
                ) : (
                  <span style={{ fontWeight: 600, fontSize: 'var(--t-13)' }}>{c.subject}</span>
                )}
                {isSent ? (
                  <span className="badge badge-sm badge-ok" title={c.sent_marked_by ? t('comms.markedByTooltip', { name: c.sent_marked_by }) : undefined}>
                    {t('comms.sentBadgeLong', { date: c.sent_at ? fd.dateTime(c.sent_at) : '—' })}
                  </span>
                ) : (
                  <span className="badge badge-sm">{t('comms.draftBadge')}</span>
                )}
              </div>
              <span style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)' }}>
                {t('comms.generatedOn', { date: c.generated_at ? fd.dateTime(c.generated_at) : '—' })}{c.generated_by ? ` ${t('comms.generatedBy', { name: c.generated_by })}` : ''}
              </span>
            </div>

            {/* Body */}
            <div style={{ padding: 'var(--s12)' }}>
              <div style={{ fontSize: 'var(--t-12)', color: 'var(--ink-2)', marginBottom: 'var(--s8)' }}>
                <strong>{t('comms.toLabel')}</strong> {c.to_name || '—'}
                {c.to_email && <span style={{ color: 'var(--ink-3)', marginLeft: 'var(--s4)' }}>&lt;{c.to_email}&gt;</span>}
              </div>

              {isEditing ? (
                <textarea
                  value={editDraft.body}
                  onChange={(e) => setEditDraft(d => ({ ...d, body: e.target.value }))}
                  rows={Math.min(20, Math.max(8, editDraft.body.split('\n').length + 1))}
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    fontFamily: 'var(--font-mono, ui-monospace, monospace)',
                    fontSize: 'var(--t-11)',
                    color: 'var(--ink-1)',
                    padding: 'var(--s8)',
                    borderRadius: 'var(--r2)',
                    border: '1px solid var(--accent)',
                    background: 'var(--surface)',
                    resize: 'vertical',
                  }}
                />
              ) : (
                <pre style={{
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'var(--font-mono, ui-monospace, monospace)',
                  fontSize: 'var(--t-11)',
                  color: 'var(--ink-1)',
                  margin: 0,
                  background: 'var(--bg)',
                  padding: 'var(--s8)',
                  borderRadius: 'var(--r2)',
                }}>{c.body}</pre>
              )}

              <div style={{
                marginTop: 'var(--s8)',
                display: 'flex', gap: 'var(--s8)', alignItems: 'center', flexWrap: 'wrap',
              }}>
                <button
                  type="button"
                  onClick={() => copyBody(c)}
                  className="sort-btn"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--s4)' }}
                >
                  📋 {copiedId === c.id ? t('comms.copied') : t('comms.copyButton')}
                </button>
                {c.to_email && (
                  <a
                    href={`mailto:${encodeURIComponent(c.to_email)}?subject=${encodeURIComponent(c.subject)}&body=${encodeURIComponent(c.body)}`}
                    className="sort-btn"
                    style={{ textDecoration: 'none' }}
                    title={t('comms.openInMailTooltip')}
                  >
                    ✉ {t('comms.openInMail')}
                  </a>
                )}
                {!isSent && !isEditing && (
                  <>
                    <button
                      type="button"
                      onClick={() => startEdit(c)}
                      disabled={actingId === c.id}
                      className="sort-btn"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--s4)' }}
                    >
                      ✎ {t('common.edit')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirming({ kind: 'mark-sent', comm: c })}
                      disabled={actingId === c.id}
                      style={{
                        padding: 'var(--s4) var(--s12)',
                        borderRadius: 'var(--r2)',
                        border: 'none',
                        background: 'var(--ok)',
                        color: 'var(--surface)',
                        fontSize: 'var(--t-12)',
                        fontWeight: 600,
                        cursor: actingId === c.id ? 'wait' : 'pointer',
                      }}
                    >
                      ✓ {t('comms.markSent')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirming({ kind: 'delete', comm: c })}
                      disabled={actingId === c.id}
                      style={{
                        padding: 'var(--s4) var(--s8)',
                        borderRadius: 'var(--r2)',
                        border: '1px solid var(--line)',
                        background: 'var(--surface)',
                        color: 'var(--err)',
                        fontSize: 'var(--t-11)',
                        cursor: actingId === c.id ? 'wait' : 'pointer',
                      }}
                    >
                      {t('common.delete')}
                    </button>
                  </>
                )}
                {isEditing && (
                  <>
                    <button
                      type="button"
                      onClick={() => saveEdit(c)}
                      disabled={actingId === c.id || !editDraft.subject.trim() || !editDraft.body.trim()}
                      style={{
                        padding: 'var(--s4) var(--s12)',
                        borderRadius: 'var(--r2)',
                        border: 'none',
                        background: 'var(--accent)',
                        color: 'var(--surface)',
                        fontSize: 'var(--t-12)',
                        fontWeight: 600,
                        cursor: actingId === c.id ? 'wait' : 'pointer',
                      }}
                    >
                      💾 {t('common.save')}
                    </button>
                    <button
                      type="button"
                      onClick={cancelEdit}
                      disabled={actingId === c.id}
                      className="sort-btn"
                    >
                      {t('common.cancel')}
                    </button>
                  </>
                )}
                {c.related_adjustment_id != null && (
                  <span style={{ marginLeft: 'auto', fontSize: 'var(--t-11)', color: 'var(--ink-3)' }}>
                    {t('comms.relatedAdjustment', { id: c.related_adjustment_id })}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}

      <ConfirmDialog
        open={confirming != null}
        title={confirming?.kind === 'mark-sent' ? t('comms.confirmMarkSentTitle') : t('comms.confirmDeleteTitle')}
        message={
          confirming?.kind === 'mark-sent'
            ? t('comms.confirmMarkSentMsg')
            : t('comms.confirmDeleteMsg')
        }
        confirmLabel={confirming?.kind === 'mark-sent' ? t('comms.confirmMarkSentLabel') : t('common.delete')}
        tone={confirming?.kind === 'delete' ? 'danger' : 'ok'}
        busy={actingId != null}
        onConfirm={performAction}
        onCancel={() => setConfirming(null)}
      />
    </div>
  );
}
